import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { GeneralDataService } from '../general/general-data.service';
import { OrquestadorService } from '../orquestador/orquestador.service';
import { OrquestadorInquilinoService } from '@app/tenant/services/OrquestadorInquilino/orquestador-inquilino.service';
import { SarlaftService } from '@app/sarlaft/services/sarlaft.service';
import { buildFullUrl, buildInactivityBody, buildOrchestatorOptions } from '@app/utils/new-flow/inactivity.utils';
import { Store } from '@ngrx/store';
import { selectQuoteId, selectResumeTenantData } from '@app/tenant/storage/resume.selectors';
import { selectQuoteId as selectSarlaftQuoteId } from '@app/sarlaft/storage/property.selectors';

type ValidKeys = 'PolicyHolder' | 'Tenant' | 'Property' | 'ProductCode';
type AllowedKeyCombinations = ValidKeys[];

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private inactivityTimer: any;
  private readonly INACTIVITY_TIMEOUT = environment.LOCAL_TIMER;
  private readonly INACTIVITY_TIMEOUT_SECOND = environment.LOCAL_TIMER_SECOND;
  private isSecondInactivity: boolean = false;
  private redirectRoute: string = 'cotizacion-pausa-inactivo'; // Ruta por defecto
  private onTimeoutCallback?: () => void;
  private beforeRedirectCallback?: () => void; // Callback para guardar datos antes de redirigir
  private screenPercentage: string = '';
  private leadRequirement: string = '';
  private keys: AllowedKeyCombinations = ['PolicyHolder'];
  private eventListeners: (() => void)[] = [];
  private estadoRescate: 'Habilitado' | 'Inhabilitado' = 'Inhabilitado'; // Habilitado solo cuando el usuario contacta al asesor
  private screenName: string = ''; // Nombre de la pantalla actual
  private originalScreenName: string = ''; // Nombre de la pantalla donde ocurrió la primera inactividad
  private originalUrl: string = ''; // URL de la pantalla donde ocurrió la primera inactividad
  private originalLeadRequirement: string = ''; // leadRequirement de la primera inactividad
  optionalProducts: object | undefined;


  generalDataService = inject(GeneralDataService);
  orquestadorService = inject(OrquestadorService);
  orquestadorInquilinoService = inject(OrquestadorInquilinoService);
  sarlaftService: SarlaftService = inject(SarlaftService);
  private store = inject(Store);

  asesorCode: string = '';
  bitacoraId: string = '';
  requesBody: any;
  private useTenantOrchestrator: boolean = false; // Indica si debe usar el orquestador de tenant


  constructor(private router: Router) { }

  /**
   * Inicia el timer de inactividad
   * @param redirectRoute Ruta a la que redirigir cuando se agote el tiempo (opcional)
   * @param onTimeout Callback a ejecutar antes de la redirección (opcional)
   * @param keys Arreglo de strings que especifica qué datos incluir (opcional)
   * @param screenPercentage Porcentaje de pantalla (opcional)
   * @param leadRequirement Requerimiento de lead (opcional)
   * @param optionalProducts Productos opcionales (opcional)
   * @param useSecondTimer Indica si se debe usar el timer de segunda inactividad (30 segundos)
   * @param beforeRedirect Callback para guardar datos del formulario antes de redirigir (opcional)
   * @param screenName Nombre de la pantalla donde se genera la inactividad (opcional)
   */
  startInactivityTimer(
    redirectRoute?: string,
    onTimeout?: () => void,
    keys?: AllowedKeyCombinations,
    screenPercentage?: string,
    leadRequirement?: string,
    optionalProducts?: object,
    useSecondTimer?: boolean,
    beforeRedirect?: () => void,
    screenName?: string
  ): void {
    if (redirectRoute) {
      this.redirectRoute = redirectRoute;
    }

    if (onTimeout) {
      this.onTimeoutCallback = onTimeout;
    }

    if (screenPercentage) {
      this.screenPercentage = screenPercentage;
    }

    if (leadRequirement) {
      this.leadRequirement = leadRequirement;
    }

    if (keys) {
      this.keys = keys;
    }

    if (optionalProducts) {
      this.optionalProducts = optionalProducts;
    }

    if (useSecondTimer !== undefined) {
      this.isSecondInactivity = useSecondTimer;
    }

    if (beforeRedirect) {
      this.beforeRedirectCallback = beforeRedirect;
    }

    if (screenName) {
      this.screenName = screenName;
      // Si no es el segundo timer, guardar la pantalla original y su leadRequirement
      if (!useSecondTimer) {
        this.originalScreenName = screenName;
        // Excluir el parámetro recordId de la URL de retoma
        this.originalUrl = buildFullUrl(this.router.url, ['recordId']);
        this.originalLeadRequirement = leadRequirement || '';
        console.log('URL de retoma guardada (sin recordId):', this.originalUrl);
      }
    }

    console.log('inactivityKeys:', keys);
    console.log('useSecondTimer:', useSecondTimer);


    this.setupTimer(keys);
    this.setupEventListeners();
  }

  /**
   * Resetea el timer de inactividad
   */
  newresetTimer(
    keys?: AllowedKeyCombinations,
    screenPercentage?: string,
    leadRequirement?: string
  ): void {
    if (keys) {
      this.keys = keys;
    }

    if (screenPercentage) {
      this.screenPercentage = screenPercentage;
    }

    if (leadRequirement) {
      this.leadRequirement = leadRequirement;
    }

    this.clearTimer();
    this.setupTimer(keys);
  }

  /**
   * Detiene el timer de inactividad
   */
  stopTimer(): void {
    this.clearTimer();
    this.removeEventListeners();
  }

  /**
   * Cambia la ruta de redirección
   * @param route Nueva ruta de redirección
   */
  setRedirectRoute(route: string): void {
    this.redirectRoute = route;
  }

  /**
   * Obtiene el tiempo de inactividad configurado
   */
  getInactivityTimeout(): number {
    return this.isSecondInactivity ? this.INACTIVITY_TIMEOUT_SECOND : this.INACTIVITY_TIMEOUT;
  }

  /**
   * Configura el timer interno
   */
  private setupTimer(keys?: AllowedKeyCombinations): void {
    const keysToUse = keys || this.keys;
    const timeout = this.isSecondInactivity ? this.INACTIVITY_TIMEOUT_SECOND : this.INACTIVITY_TIMEOUT;

    console.log('inactivityKeys: 7', keysToUse);
    console.log('Using timeout:', timeout, 'ms', `(${this.isSecondInactivity ? 'SECOND' : 'FIRST'} timer)`);

    this.inactivityTimer = setTimeout(() => {
      this.handleTimeout(keysToUse);
    }, timeout);
  }

  /**
   * Limpia el timer actual
   */
  private clearTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * Maneja el timeout de inactividad
   * @param keys Arreglo de strings que especifica qué datos incluir
   */
  private handleTimeout(keys: AllowedKeyCombinations = []): void {
    // Ejecutar callback para guardar datos del formulario ANTES de cualquier otra acción
    if (this.beforeRedirectCallback) {
      console.log('Ejecutando callback de guardado de formulario antes de redirección');
      this.beforeRedirectCallback();
    }

    console.log('inactivityKeys: 2', keys);
    console.log(`Tiempo de inactividad agotado, redirigiendo a ${this.redirectRoute}`);

    // Ejecutar callback personalizado si existe
    if (this.onTimeoutCallback) {
      this.onTimeoutCallback();
    }

    // Si es la primera inactividad, solo redirigir sin llamar al orquestador
    if (!this.isSecondInactivity) {
      console.log('Primera inactividad - Solo redirigiendo, sin llamar al orquestador');
      this.router.navigate([this.redirectRoute]);
      return;
    }

    // Si es segunda inactividad, hacer el llamado al orquestador
    console.log('Segunda inactividad - Llamando al orquestador antes de redirigir');
    // Detectar flujo basándose en la URL
    const isTenantFlow = this.originalUrl.includes('/inquilino/') || this.originalUrl.includes('/tenant/');
    const isSarlaftFlow = this.originalUrl.includes('/sarlaft/');
    
    console.log('🔍 Detectando flujo:', isSarlaftFlow ? 'SARLAFT' : (isTenantFlow ? 'TENANT' : 'NEW-FLOW'));

    if (isSarlaftFlow) {
      // Flujo de SARLAFT - usar servicio de sarlaft
      console.log('📤 Usando SarlaftService');

      // Obtener quoteId del store de sarlaft
      this.store.select(selectSarlaftQuoteId).subscribe(quoteId => {
        // Construir body para sarlaft con la estructura correcta
        this.requesBody = {
          isInactivity: true,
          quoteId: quoteId || '',
          estadoRescate: this.estadoRescate,
          estadoRetoma: 'Habilitado',
          url: this.originalUrl,
          pantalla: this.originalScreenName || 'Sin nombre',
          leadRequirement: this.originalLeadRequirement
        };

        console.log('Body para segunda inactividad (sarlaft):', this.requesBody);

        this.sarlaftService.getoptService(this.requesBody).subscribe(
          (response) => {
            console.log('SEGUNDA INACTIVIDAD (SARLAFT) - Respuesta del orquestador:', response);
            this.router.navigate([this.redirectRoute]);
          },
          (error) => {
            console.error('Error al llamar orquestador sarlaft en segunda inactividad:', error);
            this.router.navigate([this.redirectRoute]);
          }
        );
      }).unsubscribe();
    } else if (isTenantFlow) {
      // Flujo de TENANT - usar orquestador de inquilino
      console.log('📤 Usando OrquestadorInquilinoService');

      // Obtener quoteId y datos del tenant del store
      this.store.select(selectQuoteId).subscribe(quoteId => {
        this.store.select(selectResumeTenantData).subscribe(tenantData => {
          // Construir body para tenant con los datos del formulario
          this.requesBody = {
            isInactivity: true,
            quoteId: quoteId || '',
            estadoRescate: this.estadoRescate,
            estadoRetoma: 'Habilitado',
            url: this.originalUrl,
            pantalla: this.originalScreenName || 'Sin nombre',
            leadRequirement: this.originalLeadRequirement,
            screenPercentage: this.screenPercentage,
          };

          // 🔹 Agregar datos del tenant si existen (datos del formulario)
          if (tenantData) {
            console.log('📋 Incluyendo datos del Tenant en el body:', tenantData);
            this.requesBody = {
              ...this.requesBody,
              
              FirstName: tenantData.FirstName || '',
              SecondName: tenantData.SecondName || '',
              LastName: tenantData.LastName || '',
              SecondLastName: tenantData.SecondLastName || '',
              MobilePhone: tenantData.MobilePhone || '',
              Pais: tenantData.Country || 'Colombia',
              TipoIdentificacion: tenantData.DocumentType || 'C',
              NumeroIdentificacion: tenantData.DocumentNumber || '',
              FechaExpedicion: tenantData.IdIssuanceDate || '',
              CorreoElectronico: tenantData.Mail || ''
            };
          } else {
            console.log('⚠️ No hay datos de Tenant en el store');
          }

          console.log('Body para segunda inactividad (tenant):', this.requesBody);

          this.orquestadorInquilinoService.Lease_orchesResumesTenant(this.requesBody).subscribe(
            (response) => {
              console.log('SEGUNDA INACTIVIDAD (TENANT) - Respuesta del orquestador:', response);
              this.router.navigate([this.redirectRoute]);
            },
            (error) => {
              console.error('Error al llamar orquestador inquilino en segunda inactividad:', error);
              this.router.navigate([this.redirectRoute]);
            }
          );
        }).unsubscribe();
      }).unsubscribe();
    } else {
      // Flujo de NEW-FLOW - usar orquestador normal
      console.log('📤 Usando OrquestadorService');

      const isDatosPropietarioScreen = this.originalScreenName === 'Datos-propietario';
      console.log('Pantalla original:', this.originalScreenName, '- Enviar getLeadDuplicated:', isDatosPropietarioScreen);

      const options = buildOrchestatorOptions(
        keys,
        this.leadRequirement || '',
        this.asesorCode,
        this.bitacoraId,
        this.screenPercentage,
        this.optionalProducts,
        isDatosPropietarioScreen
      );

      this.generalDataService.getbody(options).subscribe((body: any) => {
        console.log('options:', JSON.stringify(options));
        console.log('Generated body:', body);

        this.requesBody = buildInactivityBody(body, {
          estadoRescate: this.estadoRescate,
          estadoRetoma: 'Habilitado',
          url: this.originalUrl,
          pantalla: this.originalScreenName || 'Sin nombre',
          leadRequirement: this.originalLeadRequirement,
          optionalProducts: this.optionalProducts,
          getLeadDuplicated: isDatosPropietarioScreen
        });

        console.log('Body para segunda inactividad:', this.requesBody);

        this.orquestadorService.postOrquestador(this.requesBody).subscribe(
          (response) => {
            console.log('SEGUNDA INACTIVIDAD - Respuesta del orquestador:', response);

            if (isDatosPropietarioScreen && response?.data?.Politica === 'Politica de lead duplicados') {
              console.log('Lead duplicado detectado en segunda inactividad - Redirigiendo a lo-sentimos');
              this.router.navigate(['seguros/hogar/arriendo/cotizador/lo-sentimos']);
              return;
            }

            this.router.navigate([this.redirectRoute]);
          },
          (error) => {
            console.error('Error al llamar orquestador en segunda inactividad:', error);
            this.router.navigate([this.redirectRoute]);
          }
        );
      });
    }
  }

  /**
   * Configura los event listeners para detectar actividad del usuario
   */
  private setupEventListeners(): void {
    this.removeEventListeners(); // Remover listeners existentes primero

    const resetTimer = () => {
      this.clearTimer();
      this.setupTimer(this.keys);
    };

    const events = ['mousemove', 'keypress', 'click', 'wheel'];

    events.forEach(eventType => {
      const listener = resetTimer.bind(this);
      document.addEventListener(eventType, listener);
      this.eventListeners.push(() => document.removeEventListener(eventType, listener));
    });
  }

  /**
   * Remueve todos los event listeners
   */
  private removeEventListeners(): void {
    this.eventListeners.forEach(removeListener => removeListener());
    this.eventListeners = [];
  }

  updateOptionalProducts(optionalProducts: object) {
    this.optionalProducts = optionalProducts;
  }

  /**
   * Habilita el estado de rescate cuando el usuario contacta al asesor
   */
  enableEstadoRescate(): void {
    this.estadoRescate = 'Habilitado';
  }

  /**
   * Deshabilita el estado de rescate
   */
  disableEstadoRescate(): void {
    this.estadoRescate = 'Inhabilitado';
  }

  /**
   * Resetea todos los estados (estadoRescate y estadoRetoma)
   * Útil cuando el usuario regresa atrás en el flujo
   */
  resetStates(): void {
    this.estadoRescate = 'Inhabilitado';
    this.isSecondInactivity = false;
    console.log('Estados reseteados: estadoRescate=Inhabilitado, isSecondInactivity=false');
  }

  /**
   * Limpia la información de la pantalla original
   * Útil cuando se inicia un nuevo flujo de inactividad
   */
  clearOriginalScreen(): void {
    this.originalScreenName = '';
    this.originalUrl = '';
    this.originalLeadRequirement = '';
    console.log('Información de pantalla original limpiada');
  }

  /**
   * Obtiene el nombre de la pantalla original donde ocurrió la primera inactividad
   * @returns Nombre de la pantalla original
   */
  getOriginalScreenName(): string {
    return this.originalScreenName;
  }

  /**
   * Obtiene la URL original donde ocurrió la primera inactividad
   * @returns URL original
   */
  getOriginalUrl(): string {
    return this.originalUrl;
  }

  /**
   * Obtiene el leadRequirement original de la primera pantalla de inactividad
   * @returns leadRequirement original
   */
  getOriginalLeadRequirement(): string {
    return this.originalLeadRequirement;
  }

  /**
   * Obtiene las keys configuradas originalmente para la inactividad
   * @returns Array de keys
   */
  getInactivityKeys(): AllowedKeyCombinations {
    return this.keys;
  }
}
